import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Download, Upload, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = '/api';

// Export every email-less biotech as a fill-in sheet; upload it back to attach emails.
export const EmailFillBackPanel = ({ missing, onApplied }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');

  const download = async () => {
    setBusy('export');
    try {
      const r = await axios.get(`${API}/audit/email-coverage/export.xlsx`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `biotechs_missing_email_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e?.response?.data?.detail;
      if (!detail && e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail; } catch { /* noop */ }
      }
      toast.error(detail ? `Export failed: ${detail}` : `Export failed (HTTP ${e?.response?.status || 'network'})`);
    } finally { setBusy(''); }
  };

  const run = async (dryRun) => {
    if (!file) return;
    setBusy(dryRun ? 'preview' : 'apply');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/audit/email-coverage/fill-back?dry_run=${dryRun ? 1 : 0}`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
      if (!dryRun) {
        toast.success(`Attached ${r.data.updated} emails (${r.data.contacts_added} new contacts, ${r.data.contacts_upgraded} existing contacts completed)`);
        setFile(null); if (fileRef.current) fileRef.current.value = '';
        onApplied?.();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); } finally { setBusy(''); }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3" data-testid="email-fill-back-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-800">
          <span className="font-semibold">Fix all missing emails in bulk.</span>{' '}
          <span className="text-slate-600">1 · Download the fill-in sheet ({missing ?? '—'} companies, known contact names pre-filled). 2 · Add emails in the yellow columns. 3 · Upload it back — preview first, then apply.</span>
        </div>
        <Button size="sm" variant="outline" onClick={download} disabled={!!busy} className="bg-white" data-testid="email-fill-back-download">
          {busy === 'export' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Download fill-in sheet
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); }}
          className="text-xs text-slate-600 file:mr-2 file:rounded-md file:border file:border-slate-300 file:bg-white file:px-2.5 file:py-1 file:text-xs" data-testid="email-fill-back-file" />
        <Button size="sm" variant="outline" onClick={() => run(true)} disabled={!file || !!busy} className="bg-white" data-testid="email-fill-back-preview">
          {busy === 'preview' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Preview changes
        </Button>
        {preview && preview.dry_run && preview.updated > 0 && (
          <Button size="sm" onClick={() => run(false)} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="email-fill-back-apply">
            {busy === 'apply' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Apply {preview.updated} emails
          </Button>
        )}
      </div>
      {preview && (
        <div className="text-xs text-slate-700 space-y-1" data-testid="email-fill-back-summary">
          <p>
            {preview.rows} rows · {preview.with_email} with an email ·{' '}
            <b className="text-emerald-700">{preview.updated} {preview.dry_run ? 'will be attached' : 'attached'}</b>{' '}
            ({preview.contacts_upgraded} existing contacts completed, {preview.contacts_added} new contacts) ·{' '}
            {preview.skipped_duplicate} already present · {preview.skipped_invalid} invalid ·{' '}
            <b className="text-rose-700">{preview.skipped_blocked} blocked</b> · {preview.not_found} company not found
          </p>
          {preview.blocked?.length > 0 && (
            <p className="flex items-start gap-1 text-rose-700"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Skipped (suppressed / Do-Not-Contact): {preview.blocked.map(b => `${b.company} – ${b.email}`).join('; ')}
            </p>
          )}
          {preview.not_found_names?.length > 0 && <p className="text-slate-500">Not found: {preview.not_found_names.join('; ')}</p>}
        </div>
      )}
    </div>
  );
};
